import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  IconButton
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  PersonOff as PersonOffIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Language as WebIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

interface ErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errorCode: string;
  errorMessage: string;
}

const ErrorDialog: React.FC<ErrorDialogProps> = ({
  open,
  onClose,
  errorCode,
  errorMessage
}) => {
  console.log('🔍 ErrorDialog Debug:', { open, errorCode, errorMessage });
  const getIcon = () => {
    switch (errorCode) {
      case 'DOMAIN_NOT_AUTHORIZED':
        return <BusinessIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
      case 'PUBLIC_EMAIL_NOT_ALLOWED':
        return <EmailIcon sx={{ fontSize: 60, color: 'info.main' }} />;
      case 'USER_NOT_FOUND':
        return <PersonOffIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
      case 'EMAIL_NOT_VERIFIED':
        return <EmailIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
      case 'ACCOUNT_DEACTIVATED':
        return <EmailIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
      default:
        return <ErrorIcon sx={{ fontSize: 60, color: 'error.main' }} />;
    }
  };

  const getTitle = () => {
    switch (errorCode) {
      case 'DOMAIN_NOT_AUTHORIZED':
        return 'Su empresa no está registrada';
      case 'PUBLIC_EMAIL_NOT_ALLOWED':
        return 'Correo Personal No Permitido';
      case 'USER_NOT_FOUND':
        return 'Usuario No Encontrado';
      case 'EMAIL_NOT_VERIFIED':
        return 'Email No Verificado';
      case 'ACCOUNT_DEACTIVATED':
        return 'Cuenta Desactivada';
      default:
        return 'Error de Autenticación';
    }
  };

  const getColor = () => {
    switch (errorCode) {
      case 'DOMAIN_NOT_AUTHORIZED':
      case 'USER_NOT_FOUND':
      case 'EMAIL_NOT_VERIFIED':
      case 'ACCOUNT_DEACTIVATED':
        return 'warning.main';
      case 'PUBLIC_EMAIL_NOT_ALLOWED':
        return 'info.main';
      default:
        return 'error.main';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: 2,
          borderColor: getColor(),
          position: 'relative'
        }
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          {getIcon()}
          <Typography variant="h5" component="h2" fontWeight="bold" color={getColor()}>
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
          {errorCode === 'DOMAIN_NOT_AUTHORIZED' 
            ? 'El dominio de su email no está registrado en nuestro sistema.'
            : errorCode === 'PUBLIC_EMAIL_NOT_ALLOWED'
            ? 'Los correos personales (Gmail, Yahoo, Outlook, etc.) no pueden acceder al portal empresarial.'
            : errorCode === 'USER_NOT_FOUND'
            ? errorMessage
            : errorMessage
          }
        </Typography>

        {errorCode === 'DOMAIN_NOT_AUTHORIZED' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              ¿Necesita acceso para su empresa?
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Si su empresa requiere acceder al portal de formación en ciberseguridad, 
              contacte a nuestro equipo:
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="primary" />
                <Link 
                  href="mailto:cramirez@vigilantium.com" 
                  color="primary" 
                  underline="hover"
                  sx={{ fontWeight: 'bold' }}
                >
                  cramirez@vigilantium.com
                </Link>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WebIcon color="primary" />
                <Link 
                  href="https://vigilantium.com" 
                  target="_blank" 
                  color="primary" 
                  underline="hover"
                  sx={{ fontWeight: 'bold' }}
                >
                  vigilantium.com
                </Link>
              </Box>
            </Box>
          </Box>
        )}

        {errorCode === 'PUBLIC_EMAIL_NOT_ALLOWED' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              ¿Trabajas en una empresa?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Por favor, utilice el correo corporativo de su empresa para acceder al sistema de formación en ciberseguridad.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              Ejemplo: juan.perez@miempresa.com
            </Typography>
          </Box>
        )}

        {errorCode === 'USER_NOT_FOUND' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              ¿No tienes una cuenta?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Regístrate para acceder al portal de formación
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
        {errorCode === 'USER_NOT_FOUND' && (
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            sx={{ 
              mr: 2,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
              }
            }}
          >
            Crear Cuenta
          </Button>
        )}
        
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          color={errorCode === 'DOMAIN_NOT_AUTHORIZED' || errorCode === 'USER_NOT_FOUND' ? 'warning' : errorCode === 'PUBLIC_EMAIL_NOT_ALLOWED' ? 'info' : 'error'}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorDialog;
